import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { api } from "../../api/apiClient";
import type { HaciendaBalance, HaciendaBalanceFigures, Location } from "../../api/types";
import { money } from "../../i18n/intl";
import { useAuth } from "../../state/AuthContext";
import { businessToday } from "../../utils/businessDay";
import { Select } from "../ui/Select";
import { StatTile } from "../ui/StatTile";
import { MonthNav, monthLabel } from "./MonthNav";
import { quarterLabel, quarterMonthsText } from "./quarterText";

/**
 * G-171 — "Balance with Hacienda" (owner only): the month and its quarter.
 *
 * Balance = IVA collected on sales − the IVA printed on counted facturas.
 * IVA paid with no factura (typed with no delivery, "No factura") is not
 * deductible (OD-164): shown, never subtracted; IVA still waiting for a
 * factura is shown apart. The quarter (modelo 303) is the quarter's months
 * added. All arithmetic is the server's (`finances/sales-iva.service.ts`).
 */
export function HaciendaBalanceCard() {
  const { hasRole } = useAuth();
  const isOwner = hasRole("OWNER");

  const today = useMemo(() => businessToday(), []);
  const [year, setYear] = useState(today.year);
  const [month, setMonth] = useState(today.month);
  const [locationId, setLocationId] = useState("");
  const [locations, setLocations] = useState<Location[]>([]);
  const [balance, setBalance] = useState<HaciendaBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const shopSelectId = useId();

  useEffect(() => {
    if (!isOwner) return;
    void api
      .listLocations()
      .then((list) => setLocations(list.filter((l) => l.isActive)))
      .catch(() => setLocations([]));
  }, [isOwner]);

  const refresh = useCallback(async () => {
    if (!isOwner) return;
    setLoading(true);
    setError(null);
    try {
      setBalance(await api.getHaciendaBalance({ year, month, locationId: locationId || undefined }));
    } catch (err: unknown) {
      setBalance(null);
      setError(err instanceof Error ? err.message : "Could not load the balance");
    } finally {
      setLoading(false);
    }
  }, [isOwner, year, month, locationId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!isOwner) return null;

  const b = balance;
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const q = b?.quarter ?? null;

  /** The balance tile for one period: "Owed to Hacienda" amber, "To recover" green. */
  const balanceTile = (title: string, f: HaciendaBalanceFigures | null, hint: string | undefined) => {
    const owed = f ? Number(f.balanceEur) : 0;
    return (
      <StatTile
        label={`${owed < 0 ? "To recover" : "Owed to Hacienda"} · ${title}`}
        value={loading || !f ? "…" : money(Math.abs(owed))}
        tone={f ? (owed > 0 ? "warning" : owed < 0 ? "positive" : "neutral") : "neutral"}
        hint={hint}
      />
    );
  };

  type Cell = { eur: string; count?: string };
  /** One period's cell: an amount and, when given, its count. */
  const cell = (f: HaciendaBalanceFigures | null, pick: (f: HaciendaBalanceFigures) => Cell) => {
    if (!f) return loading ? "…" : "—";
    const { eur, count } = pick(f);
    return count ? `${money(eur)} · ${count}` : money(eur);
  };
  /** One row of the month | quarter table. */
  const row = (label: ReactNode, pick: (f: HaciendaBalanceFigures) => Cell) => (
    <tr>
      <th scope="row">{label}</th>
      <td>{cell(b?.thisMonth ?? null, pick)}</td>
      {q ? <td>{cell(q, pick)}</td> : null}
    </tr>
  );
  /** A group's name, with the group's total when it has one. */
  const group = (title: string, pick?: (f: HaciendaBalanceFigures) => Cell) => (
    <tr className="purch-table-group">
      {pick ? (
        <>
          <th scope="rowgroup">{title}</th>
          <td>{cell(b?.thisMonth ?? null, pick)}</td>
          {q ? <td>{cell(q, pick)}</td> : null}
        </>
      ) : (
        <th scope="rowgroup" colSpan={q ? 3 : 2}>
          {title}
        </th>
      )}
    </tr>
  );
  /** The balance in words, as the tiles read it: "€6.00 owed", "€25.00 to recover". */
  const balanceCell = (f: HaciendaBalanceFigures | null) => {
    if (!f) return loading ? "…" : "—";
    const owed = Number(f.balanceEur);
    return <strong>{`${money(Math.abs(owed))} ${owed < 0 ? "to recover" : "owed"}`}</strong>;
  };

  return (
    <div className="card opcost-card purch-card">
      <div className="opcost-card-head">
        <div>
          <h3 className="opcost-card-title">Balance with Hacienda</h3>
        </div>
      </div>

      <div className="opcost-controls">
        <MonthNav
          year={year}
          month={month}
          onChange={(y, m) => {
            setYear(y);
            setMonth(m);
          }}
        />
        <div className="field opcost-shop-field">
          <label htmlFor={shopSelectId}>Shop</label>
          <Select id={shopSelectId} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            <option value="">All shops</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name} ({loc.code})
              </option>
            ))}
          </Select>
        </div>
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      <div className="stat-tile-row">
        {balanceTile(
          monthLabel(year, month),
          b?.thisMonth ?? null,
          b ? `${money(b.thisMonth.collectedEur)} collected − ${money(b.thisMonth.deductible.totalEur)} on facturas` : undefined,
        )}
        {q
          ? balanceTile(
              quarterLabel(q),
              q,
              `${quarterMonthsText(q)} · ${money(q.collectedEur)} collected − ${money(q.deductible.totalEur)} on facturas`,
            )
          : null}
      </div>

      <section className="purch-section">
        <div className="table-scroll" tabIndex={0} role="region" aria-label="Balance with Hacienda, month and quarter">
          <table className="table purch-table">
            <thead>
              <tr>
                <th scope="col">
                  <span className="visually-hidden">Figure</span>
                </th>
                <th scope="col">{monthLabel(year, month)}</th>
                {q ? <th scope="col">{quarterLabel(q)}</th> : null}
              </tr>
            </thead>
            <tbody>
              {row("IVA collected on sales", (f) => ({ eur: f.collectedEur }))}
              {/* G-173: credit notes take their IVA off, by their own date. */}
              {row("IVA on facturas · deductible", (f) => ({
                eur: f.deductible.totalEur,
                count:
                  f.deductible.creditNoteCount > 0
                    ? `${plural(f.deductible.facturaCount, "factura", "facturas")}, ${plural(
                        f.deductible.creditNoteCount,
                        "credit note",
                        "credit notes",
                      )}`
                    : plural(f.deductible.facturaCount, "factura", "facturas"),
              }))}
              <tr>
                <th scope="row">Balance</th>
                <td>{balanceCell(b?.thisMonth ?? null)}</td>
                {q ? <td>{balanceCell(q)}</td> : null}
              </tr>
              {group("Paid, not deductible · not subtracted", (f) => ({ eur: f.notDeductible.totalEur }))}
              {row("Typed, no delivery", (f) => ({
                eur: f.notDeductible.typedNoDeliveryEur,
                count: plural(f.notDeductible.typedNoDeliveryCount, "receive", "receives"),
              }))}
              {row("No factura", (f) => ({
                eur: f.notDeductible.noFacturaEur,
                count: plural(f.notDeductible.noFacturaCount, "purchase", "purchases"),
              }))}
              {group("Not subtracted yet")}
              {row("Waiting for a factura", (f) => ({
                eur: f.waitingForFactura.ivaEur,
                count: plural(f.waitingForFactura.count, "typed purchase", "typed purchases"),
              }))}
              {group("Not counted yet")}
              {row(
                <>
                  Delivery fees, with IVA <span className="badge warning">Rate not set</span>
                </>,
                (f) => ({
                  eur: f.notCounted.deliveryFees.feesEur,
                  count: plural(f.notCounted.deliveryFees.count, "delivery", "deliveries"),
                }),
              )}
              {row(
                <>
                  Running costs, as typed <span className="badge warning">No IVA basis</span>
                </>,
                (f) => ({
                  eur: f.notCounted.runningCosts.amountEur,
                  count: plural(f.notCounted.runningCosts.count, "cost row", "cost rows"),
                }),
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
